import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { debounce } from 'utils'
import { shallowEqual, useSelector } from 'react-redux'
import {
  CampaignConfigurableColumns,
  CampaignFixedColumns,
} from 'features/dashboard/table/column/campaign/CampaignColumns'
import { DashboardTypes } from 'features/Codes'
import {
  AdGroupConfigurableColumns,
  AdGroupFixedColumns,
} from 'features/dashboard/table/column/adGroup/AdGroupColumns'
import {
  keywordBiddingAmountEditColumn,
  KeywordConfigurableColumns,
  KeywordFixedColumns,
  landingUrlEditColumn,
  temporaryKeywordBiddingAmountColumn,
  temporaryLandingUrlColumn,
} from 'features/dashboard/table/column/keyword/KeywordColumns'
import {
  CreativeConfigurableColumns,
  CreativeFixedColumns,
} from 'features/dashboard/table/column/creative/CreativeColumns'
import { cloneDeep } from 'lodash'
import { KeywordTableEditTypes } from 'features/dashboard/table/keyword/keywordTableSlice'
import { GeneralColumns } from '../../../Columns'

const FixedColumns = {
  [DashboardTypes.CAMPAIGN]: CampaignFixedColumns,
  [DashboardTypes.AD_GROUP]: AdGroupFixedColumns,
  [DashboardTypes.KEYWORD]: KeywordFixedColumns,
  [DashboardTypes.CREATIVE]: CreativeFixedColumns,
}

const ConfigurableColumns = {
  [DashboardTypes.CAMPAIGN]: CampaignConfigurableColumns,
  [DashboardTypes.AD_GROUP]: AdGroupConfigurableColumns,
  [DashboardTypes.KEYWORD]: KeywordConfigurableColumns,
  [DashboardTypes.CREATIVE]: CreativeConfigurableColumns,
}

const getColumnWidth = (type, value) =>
  type === DashboardTypes.KEYWORD
    ? value?.columns[0]?.columns[0]?.width
    : value?.columns[0]?.width

const useTableColumns = () => {
  const [tableWidth, setTableWidth] = useState(1103)
  const tableRef = useRef()
  const { isExpanded: lnbExpanded } = useSelector(state => state.lnb)
  const {
    common: { type },
    tree: { visible: treeVisible },
    table: {
      config: { columns: configColumns },
      keywordTable: { editable, editType },
    },
  } = useSelector(state => state.dashboard, shallowEqual)
  const updateTableWidth = useCallback(() => {
    if (tableRef.current) {
      const width = tableRef.current.clientWidth
      if (tableWidth !== width) {
        setTableWidth(width)
      }
    }
  }, [tableWidth, tableRef])
  useEffect(() => {
    updateTableWidth()
    const debouncedUpdateFilterWidth = debounce(updateTableWidth, 200)
    window.addEventListener('resize', debouncedUpdateFilterWidth)
    return () => {
      window.removeEventListener('resize', debouncedUpdateFilterWidth)
    }
  }, [updateTableWidth])
  useEffect(() => {
    updateTableWidth()
  }, [updateTableWidth, lnbExpanded, treeVisible])
  const columns = configColumns[type]
  const fixedColumns = FixedColumns[type]
  const configurableColumns = ConfigurableColumns[type]
  const tableColumns = useMemo(() => {
    const baseConfiguredColumns = columns
      .filter(
        ({ accessor }) =>
          ![
            'temporaryKeywordBiddingAmount',
            'temporaryLandingUrl',
            'keywordBiddingAmountEdit',
            'landingUrlEdit',
          ].includes(accessor)
      )
      .map(column =>
        configurableColumns.find(({ accessor }) => accessor === column)
      )
    const configuredColumns = baseConfiguredColumns
    if (type === DashboardTypes.KEYWORD && editable) {
      const index = baseConfiguredColumns.findIndex(({ accessor }) =>
        editType === KeywordTableEditTypes.maxBiddingAmount
          ? accessor === GeneralColumns.keywordBiddingAmount.name
          : accessor === GeneralColumns.landingUrl.name
      )
      if (index === -1) {
        configuredColumns.splice(
          0,
          0,
          editType === KeywordTableEditTypes.maxBiddingAmount
            ? temporaryKeywordBiddingAmountColumn
            : temporaryLandingUrlColumn,
          editType === KeywordTableEditTypes.maxBiddingAmount
            ? keywordBiddingAmountEditColumn
            : landingUrlEditColumn
        )
      } else {
        configuredColumns.splice(
          index + 1,
          0,
          editType === KeywordTableEditTypes.maxBiddingAmount
            ? keywordBiddingAmountEditColumn
            : landingUrlEditColumn
        )
      }
    }
    const fixedColumnsWidth = fixedColumns.reduce((accumulator, value) => {
      const width = getColumnWidth(type, value)
      return accumulator + width
    }, 0)
    const configuredColumnsWidth = configuredColumns.reduce(
      (accumulator, value) => {
        const width = getColumnWidth(type, value)
        return accumulator + width
      },
      0
    )
    const whitespace = tableWidth - (fixedColumnsWidth + configuredColumnsWidth)
    if (whitespace > 0) {
      const count = configuredColumns.length + 1
      const quotient = Math.round(whitespace / count)
      const remainder = whitespace - quotient * (count - 1)
      const fixedWidthColumns = fixedColumns.slice(0, 2)
      const flexibleWidthColumns = [fixedColumns[2], ...configuredColumns]
      return [
        ...fixedWidthColumns,
        ...flexibleWidthColumns.map((value, index) => {
          const extendedWidth =
            getColumnWidth(type, value) +
            (index === count - 1 ? remainder : quotient)
          const clone = cloneDeep(value)
          if (type === DashboardTypes.KEYWORD) {
            clone.columns[0].columns[0].width = extendedWidth
          } else {
            clone.columns[0].width = extendedWidth
          }
          return clone
        }),
      ]
    } else {
      return [...fixedColumns, ...configuredColumns]
    }
  }, [
    columns,
    editable,
    editType,
    configurableColumns,
    fixedColumns,
    type,
    tableWidth,
  ])
  return {
    ref: tableRef,
    columns: tableColumns,
  }
}
export default useTableColumns
